import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Users, UserCheck, UserX, CalendarDays, RefreshCw, Monitor, Smartphone } from "lucide-react";

const relTime = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 10) return "baru saja";
  if (s < 60) return `${s} dtk lalu`;
  return `${Math.floor(s / 60)} mnt lalu`;
};

const LiveVisitorsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const { data: d } = await api.get("/admin/visitors/online");
      setData(d);
      setUpdatedAt(new Date());
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 15000);
    return () => clearInterval(t);
  }, [load]);

  const cards = [
    { label: "Sedang Online", value: data?.online ?? 0, live: true, testid: "lv-online" },
    { label: "Member Login", value: data?.members ?? 0, testid: "lv-members" },
    { label: "Tamu (Guest)", value: data?.guests ?? 0, testid: "lv-guests" },
    { label: "Hari Ini / 7 Hari", value: `${data?.today_visitors ?? 0} / ${data?.week_visitors ?? 0}`, testid: "lv-today" },
  ];

  return (
    <div data-testid="live-visitors-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-serif text-2xl flex items-center gap-2">
          <Users className="w-5 h-5 text-holy-red" /> Live Visitors
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-muted-foreground" data-testid="lv-updated-at">
            Auto-refresh 15 dtk{updatedAt ? ` · update ${updatedAt.toLocaleTimeString("id-ID")}` : ""}
          </span>
          <button
            onClick={() => load(false)}
            data-testid="lv-refresh"
            className="inline-flex items-center gap-1.5 border border-border rounded-full px-3 py-1.5 text-xs hover:border-holy-red hover:text-holy-red transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-border/60 p-5" data-testid={c.testid}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              {c.live && <span className="w-2 h-2 rounded-full bg-holy-green animate-pulse inline-block" />}
              {c.label}
            </div>
            <div className={`font-serif text-3xl mt-1 ${c.live ? "text-holy-green" : "text-foreground"}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-border/60 overflow-x-auto">
        <div className="px-4 py-3 border-b border-border/40 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-holy-green animate-pulse inline-block" />
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Siapa yang online sekarang</span>
        </div>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-cream text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Pengunjung</th>
              <th className="text-left px-4 py-3">Halaman</th>
              <th className="text-left px-4 py-3">Perangkat</th>
              <th className="text-left px-4 py-3">Bahasa</th>
              <th className="text-left px-4 py-3">Terakhir Aktif</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground text-sm">Memuat…</td></tr>
            ) : !data || data.visitors.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground text-sm" data-testid="lv-empty">Belum ada pengunjung online saat ini.</td></tr>
            ) : (
              data.visitors.map((v) => (
                <tr key={v.visitor_id} className="border-t border-border/40" data-testid={`lv-visitor-${v.visitor_id.slice(0, 8)}`}>
                  <td className="px-4 py-3">
                    {v.customer_id ? (
                      <div>
                        <div className="font-medium flex items-center gap-1.5">
                          <UserCheck className="w-3.5 h-3.5 text-holy-green" /> {v.customer_name || "Member"}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-mono">{v.visitor_id.slice(0, 8)}…</div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <UserX className="w-3.5 h-3.5" /> Tamu
                        <span className="text-[10px] font-mono">{v.visitor_id.slice(0, 8)}…</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{v.path}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs">
                      {v.device === "mobile" ? <Smartphone className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
                      {v.device === "mobile" ? "Mobile" : "Desktop"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase">{v.lang} · {v.currency}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{relTime(v.last_seen)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LiveVisitorsTab;
